'use client';

import React from 'react';
import Form from '../Form';
import type { FormField, FormFieldValidation, FormValues } from '../Form/Form.types';
import { logger } from '@/utils/logger';

interface SessionRegistrationFormProps {
  zoomSessionId: string;
  sessionTitle: string;
  onRegistered?: (data: { name: string; email: string }) => void;
  submitButtonText?: string;
}

const SessionRegistrationForm: React.FC<SessionRegistrationFormProps> = ({
  zoomSessionId,
  sessionTitle,
  onRegistered,
  submitButtonText = 'Register & View Details',
}) => {
  const emailValidation: FormFieldValidation = {
    required: 'Email is required',
    pattern: {
      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: 'Please enter a valid email address',
    },
  };

  const firstNameValidation: FormFieldValidation = {
    required: 'First name is required',
    minLength: { value: 1, message: 'Please enter your first name' },
    maxLength: { value: 100, message: 'First name must be less than 100 characters' },
  };

  const lastNameValidation: FormFieldValidation = {
    required: 'Last name is required',
    minLength: { value: 1, message: 'Please enter your last name' },
    maxLength: { value: 100, message: 'Last name must be less than 100 characters' },
  };

  const fields: FormField[] = [
    {
      name: 'firstName',
      type: 'text',
      label: 'First name',
      placeholder: 'First name',
      showLabel: true,
      validation: firstNameValidation,
      required: true,
    },
    {
      name: 'lastName',
      type: 'text',
      label: 'Last name',
      placeholder: 'Last name',
      showLabel: true,
      validation: lastNameValidation,
      required: true,
    },
    {
      name: 'email',
      type: 'email',
      label: 'Email',
      placeholder: 'Your email',
      showLabel: true,
      validation: emailValidation,
      required: true,
    },
    {
      name: 'wantsReminders',
      type: 'checkbox',
      label: "Yes, send me reminder emails (one 24 hours before the session and one 1 hour before)",
      showLabel: false, // Form renders label twice for checkboxes; we only want it beside the box
      required: false,
    },
    {
      name: 'wantsCalendarInvite',
      type: 'checkbox',
      label: 'Yes, send me calendar links for this session (Google/Outlook)',
      showLabel: false,
      required: false,
    },
  ];

  const initialValues: FormValues = {
    firstName: '',
    lastName: '',
    email: '',
    wantsReminders: true,
    wantsCalendarInvite: true,
  };

  const handleSubmit = async (
    values: FormValues,
    options?: { headers?: Record<string, string> }
  ) => {
    try {
      const headers = {
        'Content-Type': 'application/json',
        ...options?.headers,
      };

      const payload = {
        ...values,
        zoomSessionId,
      };

      const response = await fetch('/api/session-register', {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && data?.success) {
        if (onRegistered) {
          const firstName = String(values.firstName || '').trim();
          const lastName = String(values.lastName || '').trim();
          onRegistered({
            name: [firstName, lastName].filter(Boolean).join(' '),
            email: String(values.email || '').trim(),
          });
        }

        return {
          success: true,
          message:
            typeof data?.message === 'string'
              ? data.message
              : `You're registered for ${sessionTitle}.`,
        };
      }

      return {
        success: false,
        message:
          typeof data?.message === 'string'
            ? data.message
            : 'We were unable to complete your registration. Please try again.',
        errors:
          data && typeof data === 'object' && 'errors' in data
            ? (data.errors as Record<string, string>)
            : undefined,
      } as any;
    } catch (error) {
      logger.error('Error submitting session registration form:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'An error occurred while submitting your registration. Please try again.';
      return { success: false, message: errorMessage } as any;
    }
  };

  return (
    <Form
      fields={fields}
      onSubmit={handleSubmit}
      initialValues={initialValues}
      submitButtonText={submitButtonText}
      enableCSRF={true}
      enableTurnstile={true}
      turnstileSiteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY}
      turnstileResetOnSubmit={true}
    />
  );
};

export default SessionRegistrationForm;

